import api from "../../../core/api";
import { Page } from "../../../shared/models/models";
import { ReceivedOffer, ReceivedOffersQuery } from "../models/receivedOffersModels";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.servicingApiBaseUrl };
};

const serializeParams = (params: any): string => {
  const parts: string[] = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        parts.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });
  return parts.join("&");
};

class ReceivedOffersApi {
  getReceivedOffers(
    serviceRequestId: string,
    query?: Partial<ReceivedOffersQuery>
  ): Promise<Page<ReceivedOffer>> {
    return api
      .get<Page<ReceivedOffer>>(
        `/v1/customer/service-requests/${serviceRequestId}/offers`,
        {
          ...getRequestConfig(),
          params: query || { pageNo: 1, pageSize: 50 },
          paramsSerializer: serializeParams,
        }
      )
      .then((x) => x.data);
  }

  getReceivedOffer(
    serviceRequestId: string,
    offerId: string
  ): Promise<ReceivedOffer> {
    return api
      .get<ReceivedOffer>(
        `/v1/customer/service-requests/${serviceRequestId}/offers/${offerId}`,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  getReceivedOfferById(offerId: string): Promise<ReceivedOffer> {
    return api
      .get<ReceivedOffer>(
        `/v1/customer/received-offers/${offerId}`,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  acceptOffer(serviceRequestId: string, offerId: string): Promise<void> {
    return api
      .post<void>(
        `/v1/customer/service-requests/${serviceRequestId}/offers/${offerId}/accept`,
        null,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  rejectOffer(
    serviceRequestId: string,
    offerId: string,
    reason?: string
  ): Promise<void> {
    return api
      .post<void>(
        `/v1/customer/service-requests/${serviceRequestId}/offers/${offerId}/reject`,
        { reason },
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  cancelAcceptance(
    serviceRequestId: string,
    offerId: string
  ): Promise<void> {
    return api
      .post<void>(
        `/v1/customer/service-requests/${serviceRequestId}/offers/${offerId}/cancel-acceptance`,
        {},
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  async downloadCalendar(serviceRequestId: string, offerId: string): Promise<void> {
    const response = await api.get(
      `/v1/customer/service-requests/${serviceRequestId}/offers/${offerId}/calendar`,
      {
        ...getRequestConfig(),
        responseType: "blob",
      }
    );

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers["content-disposition"];
    let filename = "service-appointment.ics";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "");
      }
    }

    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { type: "text/calendar" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new ReceivedOffersApi();
